/**
 * TimeField — 시각 입력(선택형 12시간 표시, 저장은 24시간제). [HH] : [MM] 세그먼트 구조.
 *
 * 생김새·동작은 **DatePicker 와 같은 언어**를 쓴다 — 40px 한 칸 안에 고정폭 세그먼트,
 * 구분자, 포커스된 칸만 pill 로 강조. 그래서 날짜·시각 입력이 나란히 놓여도 크기가 맞는다.
 * 폭은 내용 고정이라 폼 칸 너비에 따라 제각각 늘어나지 않는다(`fullWidth` 로만 늘린다).
 *
 * `<input type="time">` 의 표기를 정하는 건 **브라우저 UI 언어**다. 한국어 Chrome 에서는
 * "오후 01:00" 으로 나오고 `lang="en-GB"` · `lang="ko-KR-u-hc-h23"` 어느 쪽으로도 바뀌지 않는다
 * (2026-09-08 실측 — Intl 은 바뀌지만 input 은 그대로). 그래서 native 대신 직접 만든다.
 *
 * 입력 규칙 (시계형 입력의 관례):
 *   - ↑ / ↓ : 그 칸을 1 씩 올리고 내린다(23→00, 59→00 으로 순환). 분은 Shift+↑↓ 로 10분 단위.
 *   - 값 앞의 [오전]/[오후] 를 누르면 12시간을 옮긴다 — hour12 사용 시 입력은 1~12, 저장은 24시간제다.
 *   - 시: 두 자리를 채우면 분으로 이동. 첫 자리가 3~9 면 두 자리가 될 수 없으므로 "0X" 로 확정하고 바로 이동.
 *   - 분: 첫 자리가 6~9 면 마찬가지로 "0X" 확정.
 *   - 분 칸이 비어 있을 때 Backspace → 시 칸으로 되돌아간다.
 *   - 값은 항상 "HH:MM" 으로만 올라간다(부분 입력 중에는 onChange 하지 않는다).
 */
import { useRef, useState } from "react";

const pad2 = (n: number): string => String(n).padStart(2, "0");
const digits = (s: string): string => s.replace(/[^\d]/g, "").slice(0, 2);

/** "HH:MM" → [시, 분] 문자열. 못 읽으면 빈 값 */
function split(value: string): [string, string] {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return ["", ""];
  return [pad2(Number(m[1])), m[2]];
}

const clampH = (n: number): number => Math.min(23, Math.max(0, n));
const clampM = (n: number): number => Math.min(59, Math.max(0, n));

interface Props {
  /** "HH:MM" (24시간제) */
  value: string;
  /** 시·분이 모두 유효할 때만, 정규화된 "HH:MM" 으로 올라온다 */
  onChange: (value: string) => void;
  disabled?: boolean;
  /** 바깥 wrapper 에 붙는다 (폭·정렬 등) */
  className?: string;
  /** 폼 칸 너비를 꽉 채운다 (기본은 내용 폭 고정 — DatePicker 와 같은 규칙) */
  fullWidth?: boolean;
  ariaLabel?: string;
  /** 오전·오후에 맞춰 1~12시로 입력. 저장값은 24시간제 유지 */
  hour12?: boolean;
}

export function TimeField({
  value,
  onChange,
  disabled,
  className,
  fullWidth,
  ariaLabel,
  hour12 = false,
}: Props) {
  const mmRef = useRef<HTMLInputElement>(null);
  const hhRef = useRef<HTMLInputElement>(null);
  /**
   * 자동 이동(시 → 분)이 일으키는 blur 를 건너뛴다.
   * focus() 는 blur 를 **동기로** 부르는데, 그 시점의 commit 은 아직 리렌더 전이라
   * 방금 입력한 값이 아니라 직전 값을 다시 써 넣어 입력이 되돌아간다.
   */
  const skipBlurRef = useRef(false);

  /** 분 칸으로 이동 — 이동 때문에 생기는 blur commit 은 무시한다 */
  function jumpToMinute() {
    skipBlurRef.current = true;
    mmRef.current?.focus();
    mmRef.current?.select();
  }

  function displayParts(raw: string): [string, string] {
    const [h, m] = split(raw);
    return [hour12 && h !== "" ? pad2(Number(h) % 12 || 12) : h, m];
  }
  const normalizeHour = (h: number) => hour12 ? Math.min(12, Math.max(1, h)) : clampH(h);
  const isPm = Number(split(value)[0]) >= 12;
  const storedHour = (h: number) => hour12 ? normalizeHour(h) % 12 + (isPm ? 12 : 0) : clampH(h);
  const [[h0, m0], setParts] = useState<[string, string]>(() => displayParts(value));
  // 밖에서 값이 바뀌면 따라간다 (effect 가 아니라 렌더 중 비교 — React 공식 패턴)
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setParts(displayParts(value));
  }

  /** 두 칸이 모두 채워졌을 때만 부모에 올린다 */
  function emit(hh: string, mm: string) {
    if (hh === "" || mm === "") return;
    const next = `${pad2(storedHour(Number(hh)))}:${pad2(clampM(Number(mm)))}`;
    if (next !== value) {
      setSeen(next);
      onChange(next);
    }
  }

  function onHourInput(raw: string) {
    const d = digits(raw);
    // 두 자리 시가 될 수 없는 첫 숫자는 확정하고 분으로 이동(12시간제 2~9, 24시간제 3~9)
    if (d.length === 1 && Number(d) >= (hour12 ? 2 : 3)) {
      const hh = pad2(Number(d));
      setParts([hh, m0]);
      emit(hh, m0);
      jumpToMinute();
      return;
    }
    setParts([d, m0]);
    if (d.length === 2) {
      const hh = pad2(normalizeHour(Number(d)));
      setParts([hh, m0]);
      emit(hh, m0);
      jumpToMinute();
    }
  }

  function onMinInput(raw: string) {
    const d = digits(raw);
    if (d.length === 1 && Number(d) >= 6) {
      const mm = pad2(Number(d));
      setParts([h0, mm]);
      emit(h0, mm);
      return;
    }
    setParts([h0, d]);
    if (d.length === 2) {
      const mm = pad2(clampM(Number(d)));
      setParts([h0, mm]);
      emit(h0, mm);
    }
  }

  /** ↑↓ 로 한 칸 올리고 내린다. 빈 칸은 현재 값(없으면 0)에서 시작하고 끝에서 순환한다 */
  function step(seg: "h" | "m", delta: number) {
    const [bh, bm] = split(value);
    const h = h0 !== "" ? storedHour(Number(h0)) : clampH(Number(bh || 0));
    const m = clampM(Number(m0 !== "" ? m0 : bm || 0));
    const hh = pad2(seg === "h" ? (((h + delta) % 24) + 24) % 24 : h);
    const mm = pad2(seg === "m" ? (((m + delta) % 60) + 60) % 60 : m);
    const next = `${hh}:${mm}`;
    setParts(displayParts(next));
    if (next !== value) {
      setSeen(next);
      onChange(next);
    }
  }

  /** 오전 ↔ 오후 (12시간 이동). 저장값은 24시간제 유지 */
  function toggleAmPm() {
    step("h", isPm ? -12 : 12);
  }

  /** 포커스를 떠날 때 한 자리·빈 칸을 정리 — 못 살리면 직전 값으로 되돌린다 */
  function commit() {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    if (h0 === "" || m0 === "") {
      setParts(displayParts(value));
      return;
    }
    const hh = pad2(normalizeHour(Number(h0)));
    const mm = pad2(clampM(Number(m0)));
    setParts([hh, mm]);
    emit(hh, mm);
  }

  return (
    <div
      className={`time-field${fullWidth ? " is-full" : ""}${disabled ? " is-disabled" : ""}${
        className ? ` ${className}` : ""
      }`}
      role="group"
      aria-label={ariaLabel}
    >
      <div
        className="tf-header"
        // 칸보다 헤더가 넓다 — 빈 곳을 눌러도 시 칸이 잡히게
        onMouseDown={(e) => {
          if (disabled || e.target !== e.currentTarget) return;
          e.preventDefault();
          hhRef.current?.focus();
          hhRef.current?.select();
        }}
      >
        <button
          type="button"
          className="tf-ampm"
          onClick={toggleAmPm}
          disabled={disabled}
          title="오전 ↔ 오후 (12시간 이동)"
          aria-label={`${isPm ? "오후" : "오전"} — 눌러서 12시간 이동`}
        >
          {isPm ? "오후" : "오전"}
        </button>
        <input
          ref={hhRef}
          className="tf-seg"
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={h0}
          placeholder="HH"
          aria-label={ariaLabel ? `${ariaLabel} 시` : "시"}
          disabled={disabled}
          spellCheck={false}
          onChange={(e) => onHourInput(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onClick={(e) => e.currentTarget.select()}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
              step("h", e.key === "ArrowUp" ? 1 : -1);
              return;
            }
            if (e.key === ":" || e.key === "." || e.key === "ArrowRight") {
              e.preventDefault();
              jumpToMinute();
            }
          }}
        />
        <span className="tf-sep" aria-hidden>
          :
        </span>
        <input
          ref={mmRef}
          className="tf-seg"
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={m0}
          placeholder="MM"
          aria-label={ariaLabel ? `${ariaLabel} 분` : "분"}
          disabled={disabled}
          spellCheck={false}
          onChange={(e) => onMinInput(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onClick={(e) => e.currentTarget.select()}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
              // Shift 는 10분 단위 — 출하 시각은 5·10분 단위로 잡는 일이 많다
              const d = e.shiftKey ? 10 : 1;
              step("m", e.key === "ArrowUp" ? d : -d);
              return;
            }
            // 빈 칸에서 지우면 시로 되돌아간다
            if ((e.key === "Backspace" && m0 === "") || e.key === "ArrowLeft") {
              e.preventDefault();
              skipBlurRef.current = true;
              hhRef.current?.focus();
              hhRef.current?.select();
            }
          }}
        />
      </div>
    </div>
  );
}
